import { useMemo } from 'react';
import {
  GetCreatorCommunitiesQuery,
  useGetCreatorCommunitiesQuery,
} from 'generated/graphql';
import { makeAsync } from '@client/new/helpers/async';
import { Async } from '@client/new/helpers/types';
import { Community, toCommunityId } from '../model/community.types';
import { MAX_ALLOWED_COMMUNITIES } from '../model/community.constants';
import { resolveInfluencerId } from '../lib/resolveInfluencerId';

const COMMUNITIES_POLL_INTERVAL_MS = 30_000;

type ServerCreator = NonNullable<GetCreatorCommunitiesQuery['creator']>;
type ServerCommunity = ServerCreator['communities'][number];

const toCommunity = (raw: ServerCommunity): Community => ({
  id: toCommunityId(raw.id),
  name: raw.name,
  description: raw.description,
  thumbnail: raw.thumbnail ?? null,
  membersCount: raw.membersCount,
  reportedPostsCount: raw.reportedPostsCount,
  blockedUsersCount: raw.blockedUsersCount,
});

const toCommunities = (
  creator: GetCreatorCommunitiesQuery['creator'],
): Community[] => {
  const communities = creator ? creator.communities.map(toCommunity) : [];

  if (communities.length > MAX_ALLOWED_COMMUNITIES) {
    console.error(
      `useCreatorCommunities: received ${communities.length} communities, exceeding MAX_ALLOWED_COMMUNITIES (${MAX_ALLOWED_COMMUNITIES})`,
    );
    // TODO: emit a Mixpanel integrity event for this overflow. The event must
    // first be added to the analytics lexicon and regenerated into events.ts
    // before trackEvent can be wired here.
  }

  return communities.slice(0, MAX_ALLOWED_COMMUNITIES);
};

export const useCreatorCommunities = (
  influencerId?: string | number,
): Async<Community[]> => {
  const creatorId = resolveInfluencerId(influencerId);

  const query = useGetCreatorCommunitiesQuery({
    variables: { id: creatorId ?? 0 },
    skip: creatorId === null,
    fetchPolicy: 'cache-and-network',
    pollInterval: COMMUNITIES_POLL_INTERVAL_MS,
  });

  // Bypasses useAsyncQuery so cache updates re-map without a loading flip.
  return useMemo(() => {
    if (query.data) {
      return makeAsync.fetched(toCommunities(query.data.creator));
    }
    if (query.error) {
      return makeAsync.error(query.error.message);
    }
    if (query.loading) {
      return makeAsync.fetching();
    }
    return makeAsync.notFetched();
  }, [query.data, query.error, query.loading]);
};
